import type { Ik, Voortgang } from '../../gedeeld/types'

/* ===========================================================================
 *  Praten met de eigen server
 *
 *  Alles loopt via de Worker op hetzelfde adres. Er gaat vanaf deze pagina
 *  geen enkel verzoek naar Supabase of naar het dashboard: de browser heeft
 *  daar geen sleutel voor en hoort die ook niet te hebben. Wat hij heeft is
 *  een koekje, en dat stuurt hij vanzelf mee.
 *
 *  Waarom er een kopie in de browser staat
 *  ---------------------------------------
 *
 *  Dit wordt gedaan op een tablet bij de wasstraat, op een telefoon in de
 *  kantine, en op de pc van kantoor. Niet overal is het net even goed. Een
 *  cursus die halverwege stilvalt omdat het opslaan van "module 3 gelezen"
 *  niet lukt, is een cursus die niemand afmaakt.
 *
 *  Dus: eerst lokaal, dan naar de server. Lukt dat laatste niet, dan blijft
 *  het lokaal staan en gaat het mee bij de volgende gelegenheid. De server
 *  blijft de baas over wat er uiteindelijk telt -- een score die daar staat
 *  wint van wat de browser denkt, want die laatste is door de gebruiker zelf
 *  te veranderen.
 * =========================================================================== */

const LOKAAL = 'tw-academie-voortgang'

/* ------------------------------------------------------------------ *
 *  De server
 * ------------------------------------------------------------------ */

export async function haalIk(): Promise<Ik | null> {
  try {
    const a = await fetch('/api/ik', { headers: { Accept: 'application/json' } })
    if (!a.ok) return null
    const lijf = await a.json() as { ok?: boolean; ik?: Ik }
    return lijf.ok && lijf.ik ? lijf.ik : null
  } catch {
    return null
  }
}

export interface VanServer {
  voortgang: Voortgang[]
  /** Kon de database bereikt worden? Zo niet, dan is een lege lijst geen bewijs. */
  bereikbaar: boolean
}

export async function haalVoortgang(): Promise<VanServer | null> {
  try {
    const a = await fetch('/api/voortgang', { headers: { Accept: 'application/json' } })
    if (!a.ok) return null
    const lijf = await a.json() as { ok?: boolean; voortgang?: Voortgang[]; bereikbaar?: boolean }
    if (!lijf.ok) return null
    return { voortgang: lijf.voortgang ?? [], bereikbaar: lijf.bereikbaar !== false }
  } catch {
    return null
  }
}

export async function stuurVoortgang(v: Voortgang): Promise<Voortgang | null> {
  try {
    const a = await fetch('/api/voortgang', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ voortgang: v }),
    })
    if (!a.ok) return null
    const lijf = await a.json() as { ok?: boolean; voortgang?: Voortgang }
    return lijf.ok ? (lijf.voortgang ?? null) : null
  } catch {
    return null
  }
}

export async function uitloggen(): Promise<void> {
  try {
    await fetch('/api/uitloggen', { method: 'POST' })
  } catch {
    /* Lukt het niet, dan is de koek er nog. Dat is vervelend en niet erg:
       hij vervalt vanzelf. De app doet hierna toch alsof je weg bent. */
  }
  wisLokaal()
}

/* ------------------------------------------------------------------ *
 *  De kopie in de browser
 *
 *  Per persoon opgeslagen. Op een gedeelde pc in de kantine logt de een uit
 *  en de ander in; zonder die sleutel ziet de tweede de voortgang van de
 *  eerste, en dat is precies de soort verwarring waarvan iemand denkt dat
 *  het systeem niet klopt.
 * ------------------------------------------------------------------ */

function sleutel(wie: string): string {
  return `${LOKAAL}:${wie}`
}

export function lokaalLezen(wie: string): Voortgang[] {
  try {
    const ruw = localStorage.getItem(sleutel(wie))
    if (!ruw) return []
    const uit = JSON.parse(ruw)
    return Array.isArray(uit) ? uit as Voortgang[] : []
  } catch {
    /* Een privévenster, een volle opslag, of iemand die het handmatig heeft
       verminkt. In alle drie de gevallen: doen alsof er niets stond. */
    return []
  }
}

export function lokaalSchrijven(wie: string, alles: Voortgang[]): void {
  try {
    localStorage.setItem(sleutel(wie), JSON.stringify(alles))
  } catch {
    /* Geen opslag beschikbaar. Dan werkt de cursus nog steeds -- alleen
       begint hij na het sluiten van het tabblad opnieuw als de server ook
       onbereikbaar was. */
  }
}

function wisLokaal(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k?.startsWith(LOKAAL)) localStorage.removeItem(k)
    }
  } catch { /* zie hierboven */ }
}

/* ------------------------------------------------------------------ *
 *  Samenvoegen
 * ------------------------------------------------------------------ */

/**
 * De stand van de server en die van de browser bij elkaar.
 *
 * Wie het wint bij een verschil is geen smaakkwestie:
 *
 *   lessen      alles wat aan één van beide kanten af is, is af. Iemand die
 *               offline drie modules las, hoort ze niet opnieuw te doen.
 *   score       de hoogste. Dezelfde regel als in de database (0147), zodat
 *               het scherm hetzelfde zegt als de lijst van kantoor.
 *   geslaagd    de vroegste datum die er is. Een behaalde toets is een feit
 *               met een datum, en die verschuift niet.
 *   pogingen    de hoogste; dat is de enige die klopt als er op twee
 *               toestellen is geoefend.
 *
 * Bij een verschil in cursusVERSIE wint de server niet en de browser niet,
 * maar het hoogste nummer: dat is de versie die er nu werkelijk staat.
 */
export function samenvoegen(server: Voortgang[], lokaal: Voortgang[]): Voortgang[] {
  const uit = new Map<string, Voortgang>()

  for (const v of [...server, ...lokaal]) {
    const er = uit.get(v.cursus)
    if (!er) { uit.set(v.cursus, { ...v }); continue }

    /* Een nieuwere versie zet de oudere opzij in plaats van ermee te
       vermengen -- anders tellen lessen mee die over andere stof gingen. */
    if (v.versie > er.versie) { uit.set(v.cursus, { ...v }); continue }
    if (v.versie < er.versie) continue

    uit.set(v.cursus, {
      cursus: v.cursus,
      versie: er.versie,
      lessen: [...new Set([...er.lessen, ...v.lessen])],
      pogingen: Math.max(er.pogingen, v.pogingen),
      besteScore: hoogste(er.besteScore, v.besteScore),
      geslaagdAt: vroegste(er.geslaagdAt, v.geslaagdAt),
      bijgewerktAt: Math.max(er.bijgewerktAt, v.bijgewerktAt),
    })
  }

  return [...uit.values()]
}

function hoogste(a: number | null, b: number | null): number | null {
  if (a === null) return b
  if (b === null) return a
  return Math.max(a, b)
}

function vroegste(a: number | null, b: number | null): number | null {
  if (a === null) return b
  if (b === null) return a
  return Math.min(a, b)
}

/* ------------------------------------------------------------------ *
 *  Een suggestie
 * ------------------------------------------------------------------ */

export interface Suggestie {
  cursus: string
  cursusNaam: string
  module: string
  /** 'fout' | 'vraag' | 'wens'; de database maakt er een wens van bij twijfel. */
  soort: string
  tekst: string
}

/**
 * Geeft het meldingsnummer terug, of null als het niet gelukt is.
 *
 * Null en niet stilzwijgend "gelukt": een bedankje bij een melding die nooit
 * is aangekomen, is de enige uitkomst die echt schade doet -- dan denkt iemand
 * dat het gemeld is en kijkt er nooit meer naar om.
 */
export async function stuurSuggestie(s: Suggestie): Promise<string | null> {
  try {
    const a = await fetch('/api/suggestie', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(s),
    })
    if (!a.ok) return null
    const lijf = await a.json() as { ok?: boolean; nummer?: string }
    return lijf.ok ? (lijf.nummer ?? '') : null
  } catch {
    return null
  }
}
